import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BRAND, NAV_LINKS } from "@/lib/constants";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#050B14]/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <a href="#home" data-testid="navbar-logo" className="flex items-center gap-3 group">
            <div className="w-11 h-11 rounded-full overflow-hidden ring-1 ring-[#D4AF37]/50 shadow-[0_0_20px_rgba(212,175,55,0.25)]">
              <img src={BRAND.logo} alt="VR Marketing Hub logo" className="w-full h-full object-cover" />
            </div>
            <div className="hidden sm:block leading-tight">
              <div className="text-white font-bold tracking-tight text-base">VR Marketing Hub</div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">Growth · Leads · Results</div>
            </div>
          </a>

          <div className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => handleNav(l.href)}
                className="text-sm text-white/80 hover:text-white transition-colors relative group"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 group-hover:w-full bg-[#D4AF37] transition-all duration-300" />
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => handleNav("#lead-form")}
              data-testid="navbar-cta-consultation"
              className="hidden md:inline-flex items-center px-5 py-2.5 rounded-full bg-[#D4AF37] hover:bg-[#F3E5AB] text-[#050B14] text-sm font-semibold transition-colors shadow-[0_0_25px_rgba(212,175,55,0.35)]"
            >
              Book FREE Consultation
            </button>
            <button
              data-testid="navbar-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden w-10 h-10 grid place-items-center rounded-full bg-white/5 border border-white/10 text-white"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="lg:hidden bg-[#050B14]/95 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => handleNav(l.href)}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  className="text-left text-white/85 hover:text-[#D4AF37] transition-colors py-1"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => handleNav("#lead-form")}
                data-testid="mobile-cta-consultation"
                className="mt-2 px-5 py-3 rounded-full bg-[#D4AF37] text-[#050B14] font-semibold"
              >
                Book FREE Consultation
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};
